/** Invoice detail: issue, send, payments, credit notes. */
import { useEffect, useState, type ChangeEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  PAYMENT_METHODS,
  type InvoiceResponse,
  type PaymentResponse,
} from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { InputText } from "@primereact/ui/inputtext";
import { Textarea } from "@primereact/ui/textarea";
import { apiDelete, apiDeleteBody, apiPatch, apiPost } from "../../lib/api.js";
import { usePermissions } from "../../lib/auth.js";
import { Field } from "../../components/ui/Field.js";
import {
  DateInput,
  Modal,
  SelectInput,
  toDateValue,
} from "../../components/ui/controls.js";
import {
  ErrorPanel,
  LoadingPanel,
  MutationError,
  formatDate,
  formatMoney,
} from "../shared.js";
import {
  DocumentPage,
  Notice,
  Panel,
  RelatedLinks,
  StatusTimeline,
  openPdf,
} from "../commercial/document.js";
import {
  LineTable,
  TotalsPanel,
  useCommercialSettings,
} from "../commercial/lines.js";
import { SendDialog } from "../offers/dialogs.js";
import { InvoiceSharing } from "./sharing.js";
import { PaymentBadge, invoiceLabel, useInvoice } from "./invoice-common.js";
import { InvoiceEditor } from "./InvoiceEditor.js";

export function PaymentDialog({
  invoice,
  payment,
  open,
  onClose,
}: {
  invoice: InvoiceResponse;
  payment: PaymentResponse | null;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [method, setMethod] = useState<string>("bank_transfer");
  const [reference, setReference] = useState("");
  useEffect(() => {
    if (!open) return;
    setAmount(
      payment?.amount ?? (Number(invoice.balance) > 0 ? invoice.balance : ""),
    );
    setDate(payment?.paymentDate ?? toDateValue(new Date()));
    setMethod(payment?.method ?? "bank_transfer");
    setReference(payment?.reference ?? "");
  }, [open, payment, invoice.balance]);
  const save = useMutation({
    mutationFn: () => {
      const body = {
        amount: amount.replace(",", "."),
        paymentDate: date,
        method,
        reference: reference.trim() || null,
      };
      return payment
        ? apiPatch<{ data: InvoiceResponse }>(`/payments/${payment.id}`, {
            ...body,
            version: payment.version,
          })
        : apiPost<{ data: InvoiceResponse }>(
            `/invoices/${invoice.id}/payments`,
            body,
          );
    },
    onSuccess: (response) => {
      queryClient.setQueryData(["invoice", invoice.id], response.data);
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () =>
      apiDelete<{ data: InvoiceResponse }>(`/payments/${payment!.id}`),
    onSuccess: (response) => {
      queryClient.setQueryData(["invoice", invoice.id], response.data);
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      onClose();
    },
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={payment ? t("payment.edit") : t("payment.record")}
      footer={
        <>
          {payment ? (
            <Button
              variant="text"
              severity="danger"
              loading={remove.isPending || undefined}
              onClick={() =>
                window.confirm(t("payment.deleteConfirm")) && remove.mutate()
              }
            >
              {t("common.delete")}
            </Button>
          ) : null}
          <Button variant="text" severity="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            loading={save.isPending || undefined}
            disabled={!(Number(amount.replace(",", ".")) > 0) || !date}
            onClick={() => save.mutate()}
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label={t("payment.amount", { currency: invoice.currency })}
          required
        >
          <InputText
            inputMode="decimal"
            value={amount}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setAmount(event.target.value)
            }
          />
        </Field>
        <Field label={t("payment.date")} required>
          <DateInput value={date} onChange={setDate} />
        </Field>
        <Field label={t("payment.method")}>
          <SelectInput
            value={method}
            onChange={setMethod}
            options={PAYMENT_METHODS.map((value) => ({
              value,
              label: t(`payment.methods.${value}`),
            }))}
          />
        </Field>
        <Field label={t("payment.reference")}>
          <InputText
            value={reference}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setReference(event.target.value)
            }
          />
        </Field>
      </div>
      <MutationError error={save.error ?? remove.error} />
    </Modal>
  );
}

export function ReasonDialog({
  open,
  title,
  hint,
  confirmLabel,
  danger,
  onClose,
  onConfirm,
  pending,
  error,
}: {
  open: boolean;
  title: string;
  hint: string;
  confirmLabel: string;
  danger?: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  pending: boolean;
  error: unknown;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (open) setReason("");
  }, [open]);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="text" severity="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            severity={danger ? "danger" : undefined}
            loading={pending || undefined}
            disabled={!reason.trim()}
            onClick={() => onConfirm(reason.trim())}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm text-app-muted">{hint}</p>
      <Field label={t("common.reason")} required>
        <Textarea
          value={reason}
          rows={3}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
            setReason(event.target.value)
          }
        />
      </Field>
      <MutationError error={error} />
    </Modal>
  );
}

export function InvoiceDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const settings = useCommercialSettings();
  const invoice = useInvoice(id);
  const [sendOpen, setSendOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [payment, setPayment] = useState<PaymentResponse | null | "new">(null);
  const [pdfError, setPdfError] = useState<unknown>(null);

  const apply = (response: { data: InvoiceResponse }) => {
    queryClient.setQueryData(["invoice", response.data.id], response.data);
    void queryClient.invalidateQueries({ queryKey: ["invoices"] });
    void queryClient.invalidateQueries({ queryKey: ["offer"] });
  };
  const issue = useMutation({
    mutationFn: () =>
      apiPost<{ data: InvoiceResponse }>(`/invoices/${id}/issue`, {
        version: invoice.data!.version,
      }),
    onSuccess: apply,
  });
  const send = useMutation({
    mutationFn: (input: { email?: string; deliver: boolean }) =>
      apiPost<{ data: InvoiceResponse }>(`/invoices/${id}/send`, {
        version: invoice.data!.version,
        ...input,
      }),
    onSuccess: (response) => {
      apply(response);
      setSendOpen(false);
    },
  });
  const cancel = useMutation({
    mutationFn: (reason: string) =>
      apiPost<{ data: InvoiceResponse }>(`/invoices/${id}/cancel`, {
        version: invoice.data!.version,
        reason,
      }),
    onSuccess: (response) => {
      apply(response);
      setCancelOpen(false);
    },
  });
  const credit = useMutation({
    mutationFn: (reason: string) =>
      apiPost<{ data: InvoiceResponse }>(`/invoices/${id}/credit-notes`, {
        reason,
      }),
    onSuccess: (response) => {
      apply(response);
      setCreditOpen(false);
      navigate(`/invoices/${response.data.id}`);
    },
  });
  const remove = useMutation({
    mutationFn: () =>
      apiDeleteBody(`/invoices/${id}`, { version: invoice.data!.version }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      void queryClient.invalidateQueries({ queryKey: ["offer"] });
      navigate("/invoices");
    },
  });

  if (invoice.isLoading) return <LoadingPanel />;
  if (invoice.error || !invoice.data)
    return <ErrorPanel error={invoice.error} />;
  const data = invoice.data;
  const isDraft = data.status === "draft";
  const isOpen = data.status === "issued" || data.status === "sent";
  const isCredit = data.kind === "credit_note";
  const showTax = settings.data?.vatRegistered !== false;
  const label = invoiceLabel(data, t);
  const pdf = (method: "GET" | "POST") => {
    setPdfError(null);
    openPdf(
      method === "POST" ? `/invoices/${id}/preview` : `/invoices/${id}/pdf`,
      method,
    ).catch(setPdfError);
  };
  const canRecord =
    isOpen && !isCredit && has("payments.record") && Number(data.balance) > 0;

  return (
    <DocumentPage
      back={{ to: "/invoices", label: t("invoice.plural") }}
      number={label}
      status={isDraft || data.status === "cancelled" ? data.status : undefined}
      title={
        data.title ||
        (isCredit ? t("invoice.creditNote") : t("invoice.singular"))
      }
      meta={
        <>
          <Link
            to={`/customers/${data.customer.id}`}
            className="hover:underline"
          >
            {data.customer.displayName}
          </Link>
          {data.invoiceDate ? ` · ${formatDate(data.invoiceDate)}` : ""}
          {data.dueDate
            ? ` · ${t("invoice.dueDate")} ${formatDate(data.dueDate)}`
            : ""}
        </>
      }
      actions={
        <>
          {isDraft ? (
            <>
              <Button
                variant="outlined"
                severity="secondary"
                icon="pi pi-eye"
                onClick={() => pdf("POST")}
              >
                {t("doc.preview")}
              </Button>
              {has("invoices.change_status") ? (
                <Button
                  icon="pi pi-check-circle"
                  loading={issue.isPending || undefined}
                  onClick={() =>
                    window.confirm(
                      isCredit
                        ? t("invoice.issueCreditConfirm")
                        : t("invoice.issueConfirm"),
                    ) && issue.mutate()
                  }
                >
                  {isCredit ? t("invoice.issueCredit") : t("invoice.issue")}
                </Button>
              ) : null}
            </>
          ) : (
            <>
              {data.pdfAvailable ? (
                <Button
                  variant="outlined"
                  severity="secondary"
                  icon="pi pi-file-pdf"
                  onClick={() => pdf("GET")}
                >
                  {t("doc.pdf")}
                </Button>
              ) : null}
              {isOpen && has("invoices.send") ? (
                <Button
                  variant={data.status === "sent" ? "outlined" : undefined}
                  severity={data.status === "sent" ? "secondary" : undefined}
                  icon="pi pi-send"
                  onClick={() => setSendOpen(true)}
                >
                  {data.status === "sent" ? t("doc.resend") : t("doc.send")}
                </Button>
              ) : null}
              {canRecord ? (
                <Button icon="pi pi-wallet" onClick={() => setPayment("new")}>
                  {t("payment.record")}
                </Button>
              ) : null}
            </>
          )}
        </>
      }
      aside={
        <>
          {isDraft && has("invoices.edit") ? null : (
            <Panel title={t("totals.heading")}>
              <TotalsPanel
                totals={data}
                currency={data.currency}
                showTax={showTax}
                totalLabel={isCredit ? t("invoice.creditTotal") : undefined}
              />
              {!isDraft && !isCredit ? (
                <dl className="mt-3 space-y-1.5 border-t border-app-border pt-3 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-app-muted">{t("invoice.paid")}</dt>
                    <dd className="tabular-nums">
                      {formatMoney(data.paidAmount, data.currency)}
                    </dd>
                  </div>
                  {Number(data.creditedAmount) > 0 ? (
                    <div className="flex justify-between">
                      <dt className="text-app-muted">
                        {t("invoice.credited")}
                      </dt>
                      <dd className="tabular-nums">
                        −{formatMoney(data.creditedAmount, data.currency)}
                      </dd>
                    </div>
                  ) : null}
                  <div className="flex justify-between font-semibold">
                    <dt>{t("invoice.balance")}</dt>
                    <dd className="tabular-nums">
                      {formatMoney(data.balance, data.currency)}
                    </dd>
                  </div>
                  <div className="pt-1">
                    <PaymentBadge invoice={data} />
                  </div>
                </dl>
              ) : null}
            </Panel>
          )}
          <Panel title={t("invoice.billingAddress")}>
            <address className="text-sm not-italic">
              {data.billing.company ? (
                <div className="font-medium">{data.billing.company}</div>
              ) : null}
              <div>{data.billing.name}</div>
              <div>{data.billing.line1}</div>
              {data.billing.line2 ? <div>{data.billing.line2}</div> : null}
              <div>
                {data.billing.postalCode} {data.billing.city}
              </div>
            </address>
            {data.reference ? (
              <p className="mt-2 font-mono text-xs text-app-muted">
                {t("invoice.reference")}: {data.reference}
              </p>
            ) : null}
          </Panel>
          <Panel title={t("doc.related")}>
            <RelatedLinks
              items={[
                ...(data.offer
                  ? [
                      {
                        label: t("offer.singular"),
                        number: data.offer.displayNumber,
                        status: data.offer.status,
                        to: `/offers/${data.offer.id}`,
                      },
                    ]
                  : []),
                ...(data.request
                  ? [
                      {
                        label: t("request.singular"),
                        number: data.request.number,
                        status: data.request.status,
                        to: `/requests/${data.request.id}`,
                      },
                    ]
                  : []),
                ...(data.project
                  ? [
                      {
                        label: t("project.singular"),
                        number: data.project.number,
                        to: `/projects/${data.project.id}`,
                      },
                    ]
                  : []),
                ...(data.correctsInvoice
                  ? [
                      {
                        label: t("invoice.corrects"),
                        number: data.correctsInvoice.number,
                        to: `/invoices/${data.correctsInvoice.id}`,
                      },
                    ]
                  : []),
                ...data.creditNotes.map((note) => ({
                  label: t("invoice.creditNote"),
                  number: note.number || t("invoice.draftCreditNote"),
                  status: note.status,
                  to: `/invoices/${note.id}`,
                })),
              ]}
            />
          </Panel>
          <StatusTimeline
            entityType="invoice"
            id={data.id}
            version={data.version}
          />
        </>
      }
    >
      {pdfError ? <MutationError error={pdfError} /> : null}
      <MutationError
        error={issue.error ?? remove.error ?? cancel.error ?? credit.error}
      />
      {isDraft &&
      !isCredit &&
      settings.data &&
      !settings.data.ibanConfigured ? (
        <Notice tone="warning">{t("invoice.ibanMissing")}</Notice>
      ) : null}
      {isDraft ? <Notice>{t("invoice.draftNotice")}</Notice> : null}
      {data.status === "cancelled" ? (
        <Notice tone="danger">
          {t("invoice.cancelledNotice", { reason: data.cancelReason ?? "" })}
        </Notice>
      ) : null}
      {data.delivery?.status === "failed" ? (
        <Notice tone="danger">
          {t("doc.deliveryFailed", { error: data.delivery.error ?? "" })}
        </Notice>
      ) : data.delivery?.status === "pending" ||
        data.delivery?.status === "processing" ? (
        <Notice>{t("doc.deliveryPending")}</Notice>
      ) : null}

      {isDraft && has("invoices.edit") ? (
        <InvoiceEditor key={data.version} invoice={data} />
      ) : (
        <>
          {data.introText ? (
            <Panel>
              <p className="whitespace-pre-line text-sm">{data.introText}</p>
            </Panel>
          ) : null}
          <Panel title={t("lines.heading")}>
            <LineTable
              items={data.items}
              currency={data.currency}
              showTax={showTax}
            />
          </Panel>
          {data.paymentTermsText ? (
            <Panel title={t("invoice.paymentTerms")}>
              <p className="whitespace-pre-line text-sm text-app-muted">
                {data.paymentTermsText}
              </p>
            </Panel>
          ) : null}
        </>
      )}

      {!isDraft && !isCredit ? (
        <Panel
          title={t("payment.plural")}
          actions={
            canRecord ? (
              <Button
                size="small"
                variant="text"
                icon="pi pi-plus"
                onClick={() => setPayment("new")}
              >
                {t("payment.record")}
              </Button>
            ) : null
          }
        >
          {data.payments.length === 0 ? (
            <p className="text-sm text-app-muted">{t("payment.none")}</p>
          ) : (
            <ul className="divide-y divide-app-border text-sm">
              {data.payments.map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <div>
                    <div className="font-medium tabular-nums">
                      {formatMoney(entry.amount, entry.currency)}
                    </div>
                    <div className="text-xs text-app-muted">
                      {formatDate(entry.paymentDate)} ·{" "}
                      {t(`payment.methods.${entry.method}`)}
                      {entry.reference ? ` · ${entry.reference}` : ""}
                    </div>
                  </div>
                  {has("payments.edit") && data.status !== "cancelled" ? (
                    <Button
                      size="small"
                      variant="text"
                      severity="secondary"
                      icon="pi pi-pencil"
                      aria-label={t("payment.edit")}
                      onClick={() => setPayment(entry)}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      ) : null}

      {isOpen && !isCredit ? (
        <InvoiceSharing invoiceId={data.id} enabled={has("invoices.send")} />
      ) : null}

      <div className="flex flex-wrap justify-end gap-2">
        {isDraft && has("invoices.delete") ? (
          <Button
            variant="text"
            severity="danger"
            icon="pi pi-trash"
            loading={remove.isPending || undefined}
            onClick={() =>
              window.confirm(t("invoice.deleteConfirm")) && remove.mutate()
            }
          >
            {t("invoice.deleteDraft")}
          </Button>
        ) : null}
        {isOpen && !isCredit && has("invoices.create") ? (
          <Button
            variant="text"
            severity="secondary"
            icon="pi pi-replay"
            onClick={() => setCreditOpen(true)}
          >
            {t("invoice.createCreditNote")}
          </Button>
        ) : null}
        {isOpen &&
        !isCredit &&
        data.payments.length === 0 &&
        Number(data.creditedAmount) === 0 &&
        has("invoices.change_status") ? (
          <Button
            variant="text"
            severity="danger"
            icon="pi pi-ban"
            onClick={() => setCancelOpen(true)}
          >
            {t("invoice.cancel")}
          </Button>
        ) : null}
      </div>

      <SendDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        defaultEmail={data.customer.email}
        onSend={(input) => send.mutate(input)}
        pending={send.isPending}
        error={send.error}
        resend={data.status === "sent"}
      />
      <ReasonDialog
        open={cancelOpen}
        title={t("invoice.cancelTitle")}
        hint={t("invoice.cancelHint")}
        confirmLabel={t("invoice.cancel")}
        danger
        onClose={() => setCancelOpen(false)}
        onConfirm={(reason) => cancel.mutate(reason)}
        pending={cancel.isPending}
        error={cancel.error}
      />
      <ReasonDialog
        open={creditOpen}
        title={t("invoice.createCreditNote")}
        hint={t("invoice.creditHint")}
        confirmLabel={t("invoice.createCreditNote")}
        onClose={() => setCreditOpen(false)}
        onConfirm={(reason) => credit.mutate(reason)}
        pending={credit.isPending}
        error={credit.error}
      />
      <PaymentDialog
        invoice={data}
        payment={payment === "new" ? null : payment}
        open={payment !== null}
        onClose={() => setPayment(null)}
      />
    </DocumentPage>
  );
}
